// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Pure text helpers for rendering message bodies safely. Kept free of the DOM so
 * they are unit-tested directly: escaping, URL linkification, and id/name utils.
 */

/** Escape a string for safe insertion as HTML text. */
export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** A segment of a message body: plain text or a hyperlink. */
export type Segment = { type: 'text'; value: string } | { type: 'link'; href: string; value: string };

const URL_RE = /(https?:\/\/[^\s<]+[^\s<.,;:!?)\]}'"])/gi;

/**
 * Split a raw message string into text and link segments. Only http(s) URLs are
 * linkified, and trailing punctuation is left out of the href. Pure — the caller
 * escapes each segment before inserting it into the DOM.
 */
export function linkify(raw: string): Segment[] {
  const out: Segment[] = [];
  let last = 0;
  for (const m of raw.matchAll(URL_RE)) {
    const idx = m.index ?? 0;
    if (idx > last) out.push({ type: 'text', value: raw.slice(last, idx) });
    const href = m[0];
    out.push({ type: 'link', href, value: href });
    last = idx + href.length;
  }
  if (last < raw.length) out.push({ type: 'text', value: raw.slice(last) });
  if (out.length === 0) out.push({ type: 'text', value: raw });
  return out;
}

/** Render a message body to safe HTML with links and preserved newlines. */
export function renderBody(raw: string): string {
  return linkify(raw)
    .map((seg) =>
      seg.type === 'link'
        ? `<a href="${escapeHtml(seg.href)}" target="_blank" rel="noopener noreferrer nofollow">${escapeHtml(seg.value)}</a>`
        : escapeHtml(seg.value).replace(/\n/g, '<br>'),
    )
    .join('');
}

/** A short, unguessable message id. */
export function makeMsgId(): string {
  const b = new Uint8Array(8);
  globalThis.crypto.getRandomValues(b);
  let out = '';
  for (const x of b) out += x.toString(16).padStart(2, '0');
  return out;
}

/** A friendly default guest name, e.g. "Guest-7F3A". */
export function randomGuestName(): string {
  const b = new Uint8Array(2);
  globalThis.crypto.getRandomValues(b);
  let hex = '';
  for (const x of b) hex += x.toString(16).padStart(2, '0');
  return `Guest-${hex.toUpperCase()}`;
}

/** A sensible filename for a received image lacking one, given its MIME type. */
export function imageFilename(name: string | undefined, mime: string | undefined, at: number): string {
  if (name && name.trim()) return name.trim();
  const ext = mime === 'image/png' ? 'png' : mime === 'image/webp' ? 'webp' : mime === 'image/gif' ? 'gif' : 'jpg';
  return `chatwell-${at}.${ext}`;
}
